import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { T, UI, FONT, BTN } from "../constants/tokens";
import { useCompanyProfileData } from "../utils/companyProfile";
import { segmentChoices, saveHoldingMark, NOT_SURE } from "../utils/holdingSegments";
import { useIsMobile } from "../utils/hooks";
import { modalScrim, modalPanel } from "./Modal";
import Icon from "./Icon";
import LTR from "./LTR";
import { regionLabelHe } from "../constants/regions";
import { Skeleton, Loading } from "./Skeleton";

// ─── "סמן תחום למעקב" (was "מה מושך אותך ב-X?" · David, 06/10/2026) ─────────
// One question, one tap. The options are the company's own business segments
// from its latest annual report, each with its share of the revenue the
// company broke down and the fiscal year. "עוד לא בטוח" is always there and
// is a real answer: it is stored, so the question is not asked again.
//
// No text field, by design. A reason typed in free text is the user's own
// thesis, and the app has nothing to check it against. A segment the company
// filed is a fact the app can follow: its share next year, a document that
// names it, a headline that touches it.
//
// Shown after a holding is added, from the holding card at any time, and from
// the home banner for holdings added before this existed. Portal to
// document.body: the mobile carousel track is a transform, and a fixed overlay
// inside it lands off-screen (see CalendarTab's EventDetail note).
//
// `onDone(result)` fires after a save: result is the stored mark or null when
// nothing could be saved. `onClose()` fires for the X / backdrop / Escape,
// with nothing stored — the question stays open for next time.
export default function SegmentPicker({ symbol, onDone, onClose }) {
  const isMobile = useIsMobile();
  const data = useCompanyProfileData(symbol);
  const [busy, setBusy] = useState(null);   // the choice being saved

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = e => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const choices = data ? segmentChoices(data.segments) : null;
  const cik = data?.company?.cik || null;

  const pick = async (choice) => {
    if (busy) return;
    setBusy(choice === NOT_SURE ? NOT_SURE : choice.name);
    const saved = await saveHoldingMark(symbol, choice, cik);
    setBusy(null);
    onDone?.(saved);
  };

  const optionStyle = (active) => ({
    display: "flex", alignItems: "center", gap: 12, width: "100%",
    padding: "12px 14px", minHeight: 52,
    background: active ? UI.state.surface : UI.surface.raised,
    border: `1px solid ${active ? UI.state.line : UI.line.hairline}`,
    borderRadius: T.radius.lg, cursor: busy ? "default" : "pointer",
    textAlign: "right", fontFamily: "inherit", color: UI.text.primary,
    opacity: busy && !active ? 0.6 : 1,
    transition: "background .15s ease",
  });

  return createPortal(
    <div dir="rtl" onClick={onClose}
      role="dialog" aria-modal="true" aria-label={`סמן תחום למעקב · ${symbol}`}
      style={modalScrim(1100, { align: isMobile ? "bottom" : "center", padding: isMobile ? 0 : 16 })}>
      <div onClick={e => e.stopPropagation()}
        style={{ ...modalPanel({ variant: isMobile ? "sheet" : "dialog", width: isMobile ? 9999 : 520 }), position: "relative", display: "flex", flexDirection: "column", gap: 12 }}>

        <button onClick={onClose} aria-label="סגור"
          style={{ position: "absolute", top: 10, left: 10, background: UI.surface.overlay, border: `1px solid ${UI.line.hairline}`, borderRadius: 6, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: UI.text.primary, padding: 0 }}>
          <Icon name="x" size={14} color={UI.text.primary}/>
        </button>

        <div style={{ paddingInlineEnd: 36 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: UI.text.primary, lineHeight: 1.3, fontFamily: FONT.heading }}>
            סמן תחום למעקב · <LTR style={{ fontFamily: FONT.latin }}>{symbol}</LTR>
          </div>
          <div style={{ fontSize: 13, color: UI.text.muted, marginTop: 4, lineHeight: 1.6 }}>
            הקשה אחת · מתוך פילוח ההכנסות שהחברה עצמה דיווחה
          </div>
        </div>

        {/* Loading */}
        {data === null && (
          <Loading>{[1, 2, 3].map(i => <Skeleton key={i} h={52} radius={T.radius.lg}/>)}</Loading>
        )}

        {/* No breakdown for this company — say so, and still take "not sure" */}
        {data !== null && !choices && (
          <div style={{ fontSize: 14, color: UI.text.secondary, lineHeight: 1.7, background: UI.surface.sunken, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.lg, padding: "12px 14px" }}>
            {data.company
              ? <>החברה אינה מפלחת את הכנסותיה לפי תחומי פעילות בדוח השנתי · אין מה לבחור מתוכו</>
              : <>אין לנו נתוני דיווח לחברה זו · <LTR>{symbol}</LTR> אינה במאגר ה-SEC שהאפליקציה מכסה</>}
          </div>
        )}

        {/* The choices */}
        {choices && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {choices.items.map(c => (
              <button key={c.name} onClick={() => pick(c)} disabled={!!busy} style={optionStyle(busy === c.name)}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, lineHeight: 1.35 }}>
                  {regionLabelHe(c.name)
                    ? <span title={c.name}>{regionLabelHe(c.name)}</span>
                    : <LTR style={{ unicodeBidi: "isolate" }}>{c.name}</LTR>}
                </span>
                <span className="si-num" style={{ fontSize: 15, fontWeight: 700, color: UI.text.primary, flexShrink: 0 }}>
                  <LTR>{c.pct}%</LTR>
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Always available */}
        {data !== null && (
          <button onClick={() => pick(NOT_SURE)} disabled={!!busy}
            style={{ ...BTN.secondary, padding: "12px 16px", minHeight: 48, fontSize: 15, opacity: busy && busy !== NOT_SURE ? 0.6 : 1 }}>
            עוד לא בטוח
          </button>
        )}

        {choices && (
          <div style={{ fontSize: 12, color: UI.text.muted, lineHeight: 1.6, paddingTop: 8, borderTop: `1px solid ${UI.line.hairline}`, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <Icon name="clipboard" size={13} color={UI.text.muted}/>
            <span>האחוזים מתוך ההכנסות שהחברה פילחה · שנת כספים <LTR>{choices.fiscalYear}</LTR> · מקור: <LTR style={{ fontWeight: 700, color: UI.text.secondary, whiteSpace: "nowrap" }}>SEC EDGAR</LTR></span>
            {choices.dropped.length > 0 && (
              <span>· שורת סיכום שהחברה דיווחה לצד חלקיה הושמטה מהחישוב</span>
            )}
          </div>
        )}

        <div style={{ fontSize: 12, color: UI.text.muted, textAlign: "center" }}>
          מידע בלבד · אינו ייעוץ השקעות
        </div>
      </div>
    </div>,
    document.body
  );
}
